import { match } from 'ts-pattern'

// --- Raw API types ---

export type DiffChange = {
  field: string
  previousValue: string
  newValue: string
}

// --- Parsed value types ---

export type ParsedJavaObject = { [key: string]: ParsedJavaValue }

export type ParsedJavaValue =
  | string
  | null
  | ParsedJavaObject
  | ParsedJavaValue[]

// --- Diff function types ---

export type DiffResult = {
  title?: string
  lines: string[]
}

export type ActivityLogDiffFn = (change: {
  field: string
  previousValue: ParsedJavaValue
  newValue: ParsedJavaValue
}) => DiffResult

// --- Public API ---

export const getFieldLabel = (field: string): string =>
  match(field)
    .with('legalName', () => 'Company name')
    .with('taxStatus', () => 'Tax status')
    .with('billingEmail', () => 'Billing email')
    .with('currency', () => 'Currency')
    .with('netPaymentTerms', () => 'Net payment terms')
    .with('dueDate', () => 'Due date')
    .with('purchaseOrderNumber', () => 'Purchase order number')
    .with('memo', () => 'Memo')
    .with('status', () => 'Status')
    .with('paymentStatus', () => 'Payment status')
    .with('billingPeriod', () => 'Billing period')
    .with('customerEmails', () => 'Customer emails')
    .with('reference', () => 'Reference')
    .with(
      'includeBeneficiaryAddressInPaymentDetails',
      () => 'Include beneficiary address in bank details'
    )
    .otherwise(() => toSentenceCase(field))

/**
 * Recursively parses a Java toString() string into a JavaScript value.
 * - "null" or empty → null
 * - "[Foo(...), Bar(...)]" → array of parsed objects
 * - "Foo(key=val, ...)" → { key: parsedVal, ... }
 * - "simple string" → string
 */
export const parseJavaValue = (value: string): ParsedJavaValue => {
  const trimmed = value.trim()

  if (!trimmed || trimmed === 'null') return null

  // Array
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    const inner = trimmed.slice(1, -1).trim()
    if (!inner) return []
    return splitTopLevelObjects(inner).map(parseJavaValue)
  }

  // Java object
  const objectInner = extractObjectInner(trimmed)
  if (objectInner !== null) {
    return parseKeyValuePairs(objectInner).reduce<ParsedJavaObject>(
      (acc, [key, val]) => ({ ...acc, [key]: parseJavaValue(val) }),
      {}
    )
  }

  return trimmed
}

/**
 * Default diff function: produces a single line per field describing
 * the change in human-readable form.
 */
export const defaultDiffFn: ActivityLogDiffFn = ({
  field,
  previousValue,
  newValue
}) => {
  const label = getFieldLabel(field)
  const prevText = formatParsedValue(previousValue)
  const newText = formatParsedValue(newValue)

  const line = match({ hasPrev: prevText !== null, hasNew: newText !== null })
    .with({ hasPrev: false, hasNew: true }, () => `${label} set to ${newText}`)
    .with({ hasPrev: true, hasNew: false }, () => `${label} removed`)
    .with(
      { hasPrev: true, hasNew: true },
      () => `${label} changed to ${newText}`
    )
    .otherwise(() => `${label} updated`)

  return { lines: [line] }
}

export const getDiffChanges = (metadata: unknown): DiffChange[] => {
  if (!metadata || typeof metadata !== 'object') return []

  const meta = metadata as Record<string, unknown>
  const diff = meta.diff as Record<string, unknown> | undefined
  if (!diff || typeof diff !== 'object') return []

  const changes = diff.changes
  if (!Array.isArray(changes)) return []

  return changes.filter(
    (change): change is DiffChange =>
      typeof change === 'object' &&
      change !== null &&
      typeof change.field === 'string' &&
      typeof change.previousValue === 'string' &&
      typeof change.newValue === 'string'
  )
}

// --- Formatting helpers ---

/**
 * Formats a parsed Java value into a human-readable string.
 * Returns null for values that can't be meaningfully displayed as a single string.
 */
export const formatParsedValue = (value: ParsedJavaValue): string | null => {
  if (value === null) return null
  if (typeof value === 'string') return value || null
  if (Array.isArray(value)) {
    const formatted = value.map(formatParsedValue).filter(Boolean)
    return formatted.length > 0 ? formatted.join(', ') : null
  }
  // Object: extract non-null scalar values
  const scalars = collectPrimitives(value)
  return scalars.length > 0 ? scalars.join(', ') : null
}

const collectPrimitives = (obj: ParsedJavaObject): string[] =>
  Object.values(obj).flatMap(val => {
    if (val === null) return []
    if (typeof val === 'string') return val ? [val] : []
    if (Array.isArray(val)) return []
    return collectPrimitives(val)
  })

// --- Low-level Java toString() parsers ---

const toSentenceCase = (field: string): string =>
  field
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .replace(/^./, char => char.toUpperCase())
    .trim()

/**
 * Splits a string containing multiple top-level Java objects separated by
 * commas, respecting balanced parentheses.
 * E.g. "Foo(a=1), Bar(b=2)" → ["Foo(a=1)", "Bar(b=2)"]
 */
const splitTopLevelObjects = (value: string): string[] => {
  const { objects, start } = value.split('').reduce(
    (acc, ch, i) => {
      if (ch === '(') return { ...acc, depth: acc.depth + 1 }
      if (ch === ')') return { ...acc, depth: acc.depth - 1 }
      if (ch === ',' && acc.depth === 0) {
        const segment = value.slice(acc.start, i).trim()
        return {
          ...acc,
          objects: segment ? [...acc.objects, segment] : acc.objects,
          start: i + 1
        }
      }
      return acc
    },
    { objects: [] as string[], depth: 0, start: 0 }
  )

  const last = value.slice(start).trim()
  return last ? [...objects, last] : objects
}

/**
 * Extracts the inner content of a Java toString() object using balanced
 * paren matching. Returns null if the value is not a Java object.
 * E.g. "Foo(a=1, b=Bar(x=2))" → "a=1, b=Bar(x=2)"
 */
const extractObjectInner = (value: string): string | null => {
  const parenIndex = value.indexOf('(')
  if (parenIndex === -1) return null
  const className = value.slice(0, parenIndex)
  if (!/^\w+$/.test(className)) return null

  const result = value
    .slice(parenIndex)
    .split('')
    .reduce<{ depth: number; endIndex: number | null }>(
      (acc, ch, i) => {
        if (acc.endIndex !== null) return acc
        const depth =
          ch === '(' ? acc.depth + 1 : ch === ')' ? acc.depth - 1 : acc.depth
        return depth === 0
          ? { ...acc, endIndex: parenIndex + i }
          : { ...acc, depth }
      },
      { depth: 0, endIndex: null }
    )

  return result.endIndex !== null
    ? value.slice(parenIndex + 1, result.endIndex)
    : null
}

/**
 * Splits Java toString() inner content into [key, value] pairs,
 * respecting balanced parentheses.
 * E.g. "key=USD, value=Foo(a=1, b=Bar(x=2))" → [["key","USD"], ["value","Foo(a=1, b=Bar(x=2))"]]
 */
const parseKeyValuePairs = (inner: string, offset = 0): [string, string][] => {
  const eqIndex = inner.indexOf('=', offset)
  if (eqIndex === -1) return []

  const key = inner.slice(offset, eqIndex).trim().replace(/^,\s*/, '')
  const valueEnd = findValueEnd(inner, eqIndex + 1)

  return [
    [key, inner.slice(eqIndex + 1, valueEnd).trim()],
    ...parseKeyValuePairs(inner, valueEnd + 1)
  ]
}

const findValueEnd = (inner: string, start: number): number =>
  inner.split('').reduce<{ pos: number; depth: number; done: boolean }>(
    (acc, ch, i) => {
      if (acc.done || i < start) return acc
      if (ch === '(') return { ...acc, depth: acc.depth + 1, pos: i + 1 }
      if (ch === ')') return { ...acc, depth: acc.depth - 1, pos: i + 1 }
      if (ch === ',' && acc.depth === 0) {
        const rest = inner.slice(i + 1).trimStart()
        if (/^\w+=/.test(rest)) return { ...acc, done: true, pos: i }
      }
      return { ...acc, pos: i + 1 }
    },
    { pos: start, depth: 0, done: false }
  ).pos
